import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useTable, useSortBy } from 'react-table';
import PageHeader from "../../../layouts/PageHeader";
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import { EmptyState } from '../../Common/EmptyState';
import { useListControls, usePersistedChoice } from '../../../../hooks/useListControls';
import { useUrlState } from '../../../../hooks/useUrlState';
import { BookOpen, Check, ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import swal from "sweetalert";
import moment from 'moment';
import VerificationDetailsModal from './VerificationDetailsModal';
import { getApiUrl } from "../../../../utils/ApiUtils";
import axiosInstance from '../../../../services/AxiosInstance';
import ImageComponent from '../../SharedComponents/ImageComponent';
import PdfThumbnail from './PdfThumbnail';
import FiscalYearPicker, { sortFiscalYears } from './FiscalYearPicker';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdd,
} from '@fortawesome/free-solid-svg-icons';

import { SplitButton, Dropdown, Spinner, Alert } from 'react-bootstrap';

const MONTH_OPTIONS = [
  { value: 'all', label: 'Alla månader' },
  ...['01','02','03','04','05','06','07','08','09','10','11','12'].map((m) => ({
    value: m,
    label: moment(m, 'MM').format('MMMM'),
  })),
];

// BFL 5:6 requires an underlag for every verifikation, so "saknar bilaga" is
// an audit question rather than a cosmetic filter.
const ATTACHMENT_OPTIONS = [
  { value: 'all', label: 'Alla verifikat' },
  { value: 'with', label: 'Med bilaga' },
  { value: 'without', label: 'Saknar bilaga' },
];

// Which flow "Nytt verifikat" opens. Picking from the dropdown sets it; the
// main button then goes straight there. Per browser, not per company: it's a
// habit, the same way list/grid is.
const CREATE_MODE_STORAGE_KEY = 'll.pref.verificationCreateMode';
const CREATE_MODE_OPTIONS = [
  { mode: 'guided', label: 'Guidad bokföring', hint: 'Steg-för-steg med mallar' },
  { mode: 'manual', label: 'Manuell bokföring', hint: 'Friare bokföring med egna konton' },
];
const CREATE_MODES = CREATE_MODE_OPTIONS.map((o) => o.mode);

export const Verifications = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [preferredCreateMode, rememberCreateMode] = usePersistedChoice(CREATE_MODE_STORAGE_KEY, 'guided', CREATE_MODES);

  const COLUMNS = [
    {
      Header: 'Underlag',
      accessor: 'attachment_id',
      id: 'attachment',
      disableSortBy: true,
      Cell: ({ row }) => <VerificationAttachment row={row} />
    },
    {
      Header: 'Verifikat',
      Footer: 'Verifikat',
      accessor: row => `${row.series}${row.number}`,
      id: 'verification',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '700',
            color: '#334155',
            fontVariantNumeric: 'tabular-nums'
          }}>
            {value}
          </span>
          {row.original.attachments?.length > 0 && (
            <span
              className="ms-2"
              style={{
                fontSize: '0.7rem',
                backgroundColor: 'var(--rgba-primary-2)',
                color: 'var(--primary-dark)',
                padding: '0.125rem 0.375rem',
                borderRadius: '0.375rem',
                fontWeight: '500'
              }}
            >
              {row.original.attachments.length}
            </span>
          )}
        </div>
      ),
    },
    {
      Header: 'Datum',
      Footer: 'Datum',
      accessor: row => moment(row.date).format('YYYY-MM-DD'),
      id: 'date',
      Cell: ({ value }) => (
        <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {value}
        </span>
      ),
    },
    {
      Header: 'Titel',
      Footer: 'Titel',
      accessor: 'description',
      id: 'description',
      Cell: ({ value }) => (
        <span style={{ color: '#1f2937', fontWeight: 600 }}>
          {value || 'Utan titel'}
        </span>
      ),
    },
    {
      Header: 'Belopp',
      Footer: 'Belopp',
      accessor: 'debitSum',
      id: 'debitSum',
      align: 'end',
      Cell: ({ value }) => (
        <span style={{ color: '#1f2937', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value)}
        </span>
      ),
    },
    {
      Header: '',
      id: 'open',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn btn-link p-1 verification-register__open"
          aria-label={`Öppna verifikat ${row.original.series}${row.original.number}`}
          onClick={(event) => {
            event.stopPropagation();
            handleViewDetails(event, row.original);
          }}
        >
          <ChevronRight size={17} />
        </button>
      ),
    },
  ];

  const columns = useMemo(() => COLUMNS, []);

  // Fetch fiscal years when component mounts
  useEffect(() => {
    fetchFiscalYears();
  }, [companyId]);

  // Fetch verifications when selectedFiscalYear changes or lastUpdate changes
  useEffect(() => {
    if (!selectedFiscalYear) return;
    fetchVerifications();
  }, [companyId, selectedFiscalYear, lastUpdate]);

  // Check for return from other views with an update flag
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('updated') === 'true') {
      // Remove the query parameter without reloading the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Trigger a refresh
      setLastUpdate(Date.now());
    }
  }, [location]);

  // Function to fetch fiscal years
  const fetchFiscalYears = async () => {
    try {
      const result = await axiosInstance.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
      const sortedYears = sortFiscalYears(result.data.fiscalYears || []);
      setFiscalYears(sortedYears);
      if (sortedYears.length > 0) {
        const latestFiscalYear = sortedYears[0];
        setSelectedFiscalYear(latestFiscalYear);
      }
    } catch (err) {
      console.error('Error fetching fiscal years:', err);
      setError("Det gick inte att hämta räkenskapsår. Försök igen senare.");
      setFiscalYears([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch verifications
  const fetchVerifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await axiosInstance.get(getApiUrl(`${companyId}/verifications/${selectedFiscalYear.year}`), { withCredentials: true });
      const updatedVerifications = result.data.verifications.map(verification => {
        let debitSum = 0;
        let creditSum = 0;

        verification.transactions.forEach(transaction => {
          if (transaction.transaction_type === 'debit') {
            debitSum += parseFloat(transaction.amount);
          } else if (transaction.transaction_type === 'credit') {
            creditSum += parseFloat(transaction.amount);
          }
        });

        return {
          ...verification,
          debitSum,
          creditSum,
          attachment: verification.attachments || null,
        };
      });

      setData(updatedVerifications);
    } catch (err) {
      console.error('Error fetching verifications:', err);
      setError("Det gick inte att hämta verifikaten. Försök igen senare.");
      setData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handler for manual refresh button
  const handleRefresh = () => {
    setRefreshing(true);
    setLastUpdate(Date.now());
  };

  const openCreateVerification = (mode, remember = false) => {
    if (remember) rememberCreateMode(mode);

    if (mode === 'manual') {
      history.push({
        pathname: `/${companyId}/manual-bookkeeping-entry`,
        state: { fiscalYear: selectedFiscalYear },
      });
      return;
    }

    history.push(`/${companyId}/create-bookkeeping-entry`);
  };

  // Handler for file upload completion - trigger refresh
  const handleAttachmentUploadComplete = useCallback(() => {
    setLastUpdate(Date.now());
  }, []);

  // Handler for verification update/deletion from modal
  const handleVerificationUpdated = useCallback(() => {
    setLastUpdate(Date.now());
  }, []);

  const handleUploadClick = (event, verificationId) => {
    event.stopPropagation(); // Prevent row click
    event.preventDefault();

    // Trigger file upload
    const fileInput = document.getElementById(`upload-input-${verificationId}`);
    if (fileInput) {
      fileInput.click();
    } else {
      console.error(`File input element not found for verificationId: ${verificationId}`);
    }
  };

  const handleFileUpload = async (event, verificationId) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const uploadFileApiUrl = getApiUrl(`${companyId}/accounting/verifications/${verificationId}/attachments`);
        const response = await axiosInstance.post(uploadFileApiUrl, formData, { withCredentials: true });

        if (response.data.success) {
          handleAttachmentUploadComplete();
          //swal("Filuppladdning lyckades", "Filen har laddats upp och kopplats till verifikatet", "success");
        } else {
          swal("Filuppladdning misslyckades", response.data.message || "Ett fel uppstod vid uppladdning av filen", "error");
        }
      } catch (error) {
        swal("Filuppladdning misslyckades", "Ett fel uppstod vid uppladdning av filen", "error");
      }
    }
  };

  const handleFiscalYearChange = (fiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
  };

  // ── Filtering ────────────────────────────────────────────────────────────
  // Verifikat have four dimensions worth narrowing on: free text (number or
  // title), the verifikatserie, the period within the fiscal year, and whether
  // an underlag is attached — the last one matters because BFL 5:6 requires a
  // verifikation to have one, so "saknar bilaga" is a real audit question.
  // Declared before useTable, which consumes visibleData.
  const {
    search,
    setSearch,
    debouncedSearch,
    filter: seriesFilter,
    setFilter: setSeriesFilter,
  } = useListControls({ defaultFilter: 'all', defaultSort: null, filterKey: 'serie' });
  const [attachmentFilter, setAttachmentFilter] = useUrlState('bilaga', 'all');
  const [monthFilter, setMonthFilter] = useUrlState('manad', 'all');
  const activeRefinementCount = Number(monthFilter !== 'all') + Number(attachmentFilter !== 'all');
  const activeMonthLabel = MONTH_OPTIONS.find((option) => option.value === monthFilter)?.label;
  const activeAttachmentLabel = ATTACHMENT_OPTIONS.find((option) => option.value === attachmentFilter)?.label;

  const hasAttachment = useCallback(
    (v) => Array.isArray(v.attachments) && v.attachments.length > 0,
    [],
  );

  // Series present in this fiscal year, in the order they appear.
  const seriesChips = useMemo(() => {
    const counts = new Map();
    data.forEach((v) => {
      const key = v.series || '—';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [
      { key: 'all', label: 'Alla', count: data.length },
      ...[...counts.entries()]
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([key, count]) => ({ key, label: `Serie ${key}`, count })),
    ];
  }, [data]);

  const visibleData = useMemo(() => {
    let rowsOut = data;

    if (seriesFilter !== 'all') {
      rowsOut = rowsOut.filter((v) => (v.series || '—') === seriesFilter);
    }

    if (attachmentFilter === 'with') rowsOut = rowsOut.filter(hasAttachment);
    if (attachmentFilter === 'without') rowsOut = rowsOut.filter((v) => !hasAttachment(v));

    if (monthFilter !== 'all') {
      rowsOut = rowsOut.filter((v) => moment(v.date).format('MM') === monthFilter);
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      // Amounts are matched separately: the query may be typed the Swedish way
      // ("50 000,00", with a space and a decimal comma) while the underlying
      // value is a plain JS number, so both sides are normalised to
      // "50000.00" before comparing. Only attempted when the query actually
      // looks numeric, so text searches don't accidentally hit an amount.
      const qNumeric = q.replace(/[\s ]/g, '').replace(',', '.');
      const searchesAmount = /^[0-9]+(\.[0-9]*)?$/.test(qNumeric);

      rowsOut = rowsOut.filter((v) => {
        if (`${v.series || ''}${v.number || ''}`.toLowerCase().includes(q)) return true;
        if ((v.description || '').toLowerCase().includes(q)) return true;
        if (searchesAmount) {
          const amount = Number(v.debitSum) || 0;
          if (amount.toFixed(2).includes(qNumeric)) return true;
          if (String(Math.round(amount)).includes(qNumeric)) return true;
        }
        return false;
      });
    }

    return rowsOut;
  }, [data, seriesFilter, attachmentFilter, monthFilter, debouncedSearch, hasAttachment]);

  const visibleTotal = useMemo(
    () => visibleData.reduce((sum, item) => sum + (Number(item.debitSum) || 0), 0),
    [visibleData],
  );
  const isFiltered = visibleData.length !== data.length;

  const tableInstance = useTable({
    columns,
    data: visibleData,
  }, useSortBy);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  const handleViewDetails = (event, row) => {
    event.preventDefault();
    setSelectedRow(row);
    setShowVerificationDetails(true);
  };

  const VerificationAttachment = ({ row }) => {
    // If this is an invoice payment verification with an invoice ID
    if (row.original.invoice?.invoice_id) {
      return (
        <PdfThumbnail
          companyId={companyId}
          invoiceId={row.original.invoice.invoice_id}
          style={{
            width: '34px',
            height: '34px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            objectFit: 'cover'
          }}
        />
      );
    }

    // Regular attachment handling
    return (
      <>
        {row.original.attachments.length > 0 ? (
          <ImageComponent
            imageUrl={getApiUrl(`${companyId}/accounting/verifications/attachments/${row.original.attachments[0].attachment_id}/thumbnail`)}
            alt=""
            style={{
              width: '34px',
              height: '34px',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}
          />
        ) : (
          <button
            type="button"
            aria-label={`Ladda upp underlag till ${row.original.series}${row.original.number}`}
            title="Ladda upp underlag"
            style={{
              width: '34px',
              height: '34px',
              border: '1px dashed #dee2e6',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: '#f8f9fa',
              padding: 0
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.backgroundColor = 'var(--rgba-primary-1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#dee2e6';
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onClick={(e) => handleUploadClick(e, row.original.verification_id)}
          >
            <FontAwesomeIcon
              icon={faAdd}
              style={{
                color: '#6c757d',
                fontSize: '16px'
              }}
            />
          </button>
        )}
        <input
          type="file"
          id={`upload-input-${row.original.verification_id}`}
          style={{ display: 'none' }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { handleFileUpload(e, row.original.verification_id) }}
          accept="application/pdf,image/*"
        />
      </>
    );
  };

  return (
    <>
      <style>{`
        .verification-register__table {
          table-layout: auto;
        }

        .verification-register__row {
          cursor: pointer;
          border-bottom: 1px solid #eef1f5;
          transition: background-color 0.15s ease;
        }

        .verification-register__row:hover,
        .verification-register__row:focus-within {
          background: var(--rgba-primary-1);
        }

        .verification-register__open {
          color: #94a3b8;
          transition: color 0.15s ease, transform 0.15s ease;
        }

        .verification-register__row:hover .verification-register__open,
        .verification-register__open:focus {
          color: var(--primary);
          transform: translateX(2px);
        }

        .verification-cell--attachment { width: 70px; }
        .verification-cell--verification { width: 120px; }
        .verification-cell--date { width: 130px; }
        .verification-cell--debitSum { width: 160px; }
        .verification-cell--open { width: 48px; }

        @media (max-width: 767.98px) {
          .verification-register__table thead { display: none; }
          .verification-register__table,
          .verification-register__table tbody { display: block; width: 100%; }
          .verification-register__row {
            display: grid !important;
            grid-template-columns: 42px minmax(0, 1fr) auto;
            grid-template-rows: auto auto auto;
            column-gap: 0.75rem;
            padding: 0.875rem;
          }
          .verification-register__row .verification-cell {
            display: block !important;
            width: auto;
            padding: 0.15rem 0 !important;
          }
          .verification-cell--attachment { grid-column: 1; grid-row: 1 / span 2; }
          .verification-cell--verification { grid-column: 2; grid-row: 1; }
          .verification-cell--date { grid-column: 2; grid-row: 2; font-size: 0.75rem !important; }
          .verification-cell--description { grid-column: 1 / span 3; grid-row: 3; padding-top: 0.5rem !important; }
          .verification-cell--debitSum { grid-column: 3; grid-row: 1; text-align: right; }
          .verification-cell--open { grid-column: 3; grid-row: 2; text-align: right; }
        }
      `}</style>
      <div className="col-12">
        <VerificationDetailsModal
          showVerificationDetails={showVerificationDetails}
          setShowVerificationDetails={setShowVerificationDetails}
          selectedRow={selectedRow}
          selectedFiscalYear={selectedFiscalYear}
          onVerificationUpdated={handleVerificationUpdated}
        />

        <PageHeader
          icon={BookOpen}
          title="Verifikationer"
          subtitle="Alla bokförda verifikat i räkenskapsåret."
        />

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {loading && !refreshing ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Laddar...</span>
            </Spinner>
          </div>
        ) : fiscalYears.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="Inga räkenskapsår ännu"
            description="Skapa ett räkenskapsår under Inställningar för att börja bokföra verifikationer."
            actions={(
              <Link to={`/${companyId}/settings/fiscal-years`} className="btn btn-primary">
                Gå till inställningar
              </Link>
            )}
          />
        ) : (
          <>
            <div className="mb-4">
              <FiscalYearPicker
                years={fiscalYears}
                selectedYear={selectedFiscalYear}
                onChange={handleFiscalYearChange}
                disabled={refreshing}
              />
            </div>

            <ListToolbar
              className="mb-3"
              search={{
                value: search,
                onChange: setSearch,
                placeholder: 'Sök verifikatnummer, titel eller belopp…',
                ariaLabel: 'Sök bland verifikationer',
              }}
              actions={(
                <SplitButton
                  id="create-verification-button"
                  title="Nytt verifikat"
                  variant="primary"
                  onClick={() => openCreateVerification(preferredCreateMode)}
                  className="split-button-custom"
                >
                  {CREATE_MODE_OPTIONS.map(({ mode, label, hint }) => {
                    const isDefault = mode === preferredCreateMode;
                    return (
                      <Dropdown.Item
                        key={mode}
                        onClick={() => openCreateVerification(mode, true)}
                        className="d-flex align-items-center justify-content-between gap-3"
                        aria-current={isDefault ? 'true' : undefined}
                      >
                        <div>
                          <div className="fw-medium">{label}</div>
                          <small className="text-muted">{hint}</small>
                        </div>
                        {isDefault && (
                          <Check size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} aria-label="Standardval" />
                        )}
                      </Dropdown.Item>
                    );
                  })}
                </SplitButton>
              )}
            >
              <Dropdown autoClose="outside" align="end">
                <Dropdown.Toggle
                  id="verification-filter-menu"
                  variant="outline-secondary"
                  className="d-inline-flex align-items-center gap-2"
                  style={{ minHeight: 38, borderColor: activeRefinementCount ? 'var(--primary)' : '#cbd5e1' }}
                >
                  <SlidersHorizontal size={15} />
                  Filter
                  {activeRefinementCount > 0 && (
                    <span
                      aria-label={`${activeRefinementCount} aktiva filter`}
                      style={{
                        minWidth: 20,
                        height: 20,
                        borderRadius: 10,
                        background: 'var(--primary)',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {activeRefinementCount}
                    </span>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-3" style={{ minWidth: 330, maxWidth: 'calc(100vw - 2rem)' }}>
                  <div className="mb-3">
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.5rem' }}>
                      Månad
                    </div>
                    <div className="ll-chips" role="group" aria-label="Filtrera på månad">
                      {MONTH_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`ll-chip ${monthFilter === option.value ? 'active' : ''}`}
                          aria-pressed={monthFilter === option.value}
                          onClick={() => setMonthFilter(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.5rem' }}>
                      Bilaga
                    </div>
                    <div className="ll-chips" role="group" aria-label="Filtrera på bilaga">
                      {ATTACHMENT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`ll-chip ${attachmentFilter === option.value ? 'active' : ''}`}
                          aria-pressed={attachmentFilter === option.value}
                          onClick={() => setAttachmentFilter(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeRefinementCount > 0 && (
                    <div className="border-top mt-3 pt-2 text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-decoration-none"
                        style={{ color: 'var(--primary)' }}
                        onClick={() => {
                          setMonthFilter('all');
                          setAttachmentFilter('all');
                        }}
                      >
                        Rensa filter
                      </button>
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </ListToolbar>

            {activeRefinementCount > 0 && (
              <div className="ll-chips mb-3" role="group" aria-label="Aktiva filter">
                {monthFilter !== 'all' && (
                  <button
                    type="button"
                    className="ll-chip active"
                    onClick={() => setMonthFilter('all')}
                    aria-label={`Ta bort månadsfilter ${activeMonthLabel}`}
                  >
                    Månad: {activeMonthLabel}
                    <X size={13} />
                  </button>
                )}
                {attachmentFilter !== 'all' && (
                  <button
                    type="button"
                    className="ll-chip active"
                    onClick={() => setAttachmentFilter('all')}
                    aria-label={`Ta bort bilagefilter ${activeAttachmentLabel}`}
                  >
                    {activeAttachmentLabel}
                    <X size={13} />
                  </button>
                )}
                {activeRefinementCount > 1 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none"
                    style={{ color: 'var(--primary)' }}
                    onClick={() => {
                      setMonthFilter('all');
                      setAttachmentFilter('all');
                    }}
                  >
                    Rensa alla
                  </button>
                )}
              </div>
            )}

            {/* Verifikatserier present in this fiscal year. */}
            {seriesChips.length > 2 && (
              <FilterChips
                className="mb-3"
                chips={seriesChips}
                value={seriesFilter}
                onChange={setSeriesFilter}
              />
            )}

            {refreshing && (
              <div className="text-center mb-3">
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Uppdaterar verifikat...
              </div>
            )}

            {data.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                title={`Inga verifikat för ${selectedFiscalYear?.year}`}
                description="Här samlas allt som bokförs — manuellt, från bankhändelser eller av Finn."
                actions={(
                  // UX_PLAN.md P8: a first-run register points at the first
                  // action instead of describing itself.
                  <>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => openCreateVerification('guided')}
                    >
                      Bokför en händelse
                    </button>
                    <Link to={`/${companyId}/settings/import-export`} className="btn btn-outline-secondary">
                      Importera från ditt gamla program
                    </Link>
                  </>
                )}
              />
            ) : visibleData.length === 0 ? (
              <EmptyState
                variant="no-matches"
                query={debouncedSearch}
                description="Byt filter för att se andra verifikat."
              />
            ) : (
              <div className="card shadow-sm border-0 mt-4" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table {...getTableProps()} className="table mb-0 verification-register__table">
                      <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {headerGroups.map(headerGroup => (
                          <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                              <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className={`fw-semibold border-0 py-3 px-3 ${column.align === 'end' ? 'text-end' : ''}`}
                                style={{
                                  display: column.hidden ? 'none' : 'table-cell',
                                  cursor: column.canSort ? 'pointer' : 'default',
                                  userSelect: 'none',
                                  fontSize: '0.875rem',
                                  letterSpacing: '0.02em',
                                  color: '#475569',
                                  backgroundColor: 'transparent'
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <span>{column.render('Header')}</span>
                                  {column.canSort && (
                                    <span className="ms-2">
                                      {column.isSorted ? (
                                        column.isSortedDesc ?
                                          <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: 'var(--primary)' }} />
                                          :
                                          <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: 'var(--primary)' }} />
                                      ) : (
                                        <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                      )}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <tr
                              {...row.getRowProps()}
                              className="verification-register__row"
                              onClick={(event) => handleViewDetails(event, row.original)}
                            >
                              {row.cells.map(cell => (
                                <td
                                  {...cell.getCellProps()}
                                  className={`verification-cell verification-cell--${cell.column.id} py-3 px-3 align-middle border-0 ${cell.column.align === 'end' ? 'text-end' : ''}`}
                                  style={{
                                    display: cell.column.hidden ? 'none' : 'table-cell',
                                    fontSize: '0.875rem',
                                    color: '#374151'
                                  }}
                                >
                                  {cell.render('Cell')}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Modern footer with clean design */}
                  <div style={{
                    backgroundColor: '#fff',
                    borderTop: '1px solid #f1f5f9',
                    padding: '1rem 1.5rem'
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {isFiltered
                          ? `Visar ${rows.length} av ${data.length} verifikationer`
                          : `Visar ${rows.length} verifikationer`}
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
                        {isFiltered ? 'Summa visade' : 'Total'}: {' '}
                        {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(visibleTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Verifications;
